import { createAsyncThunk } from '@reduxjs/toolkit';
import { errMsg } from '../utils/errMsg';
import { classifyError, reportShownError, presentError } from '../../utils/errorCodes';
import {
    setToken,
    setTokenValid,
    setTokenError,
    clearToken,
    setIsSearching,
    setDateRange,
    setSearchError,
    setArtistResults,
    setTrackResults,
    setTrackVariantCache,
    clearTrackVariantCache,
    setMixResults,
    setMixResultsPagination,
    appendMixResults,
    updateMixTracklist,
    setMixFilterContext,
    setMixPagePublicId,
    setArtistPagePublicId,
    setTrackPagePublicId,
    appendArtistMixes,
    setPendingArtist,
    setPendingTrack,
    clearExpandedMixIds,
    setTrackListResults,
    setTrackListPagination,
    appendTrackListResults,
    setTrackListContext,
    setActiveSearch,
    setTrackQuery,
    setTrackListQuery,
} from '../slices/djpalSlice';
import { addSearchHistoryEntry } from './searchHistoryThunk';
import { warmImages } from '../../utils/imageWarm';
import { coverUrl } from '../../utils/coverUrl';
import { isNative } from '../../utils/webOnly';
import { DESIGN_V2_AVAILABLE } from '../../utils/designV2';
import { isRecsVariantDevEnabled } from '../../utils/devFlags';
import { entityId, publicEntityId } from '../../utils/entityId';
import { setAccessToken, setRefreshToken, setUser } from '../slices/authSlice';
import { track } from '../../utils/analytics';

// Paginated track lists (artist page "Tracks they played" / "Their tracks
// played by others", free-text track lists) render 48px row art — start each
// landed page's covers downloading with the data, or they fetch lazily
// one-by-one as the list scrolls and visibly trail on slow connections.
const warmTrackListCovers = (arr) => warmImages(
    (arr || []).map((t) => coverUrl(t?.artworkUrl || t?.artwork || t?.image || null, 48)), 80);

// Floor between djpal-connect-driven refresh attempts. useDjpalConnect retries
// the token exchange every 8s forever while unhealthy; without this gate a
// persistently-failing exchange (auth-shaped or not) burns a fresh
// /api/auth/refresh call on every one of those retries — enough to exhaust
// the 60/hour/IP quota in under 8 minutes. See useDjpalTokenRenewal's
// MIN_ATTEMPT_GAP_MS for the same convention on the other djpal token path.
const MIN_DJPAL_REFRESH_GAP_MS = 60_000;
let _lastDjpalRefreshAttempt = 0;

// Web has no JS-readable auth tokens — credentials:'include' on the shim's
// fetch carries the mg_access cookie automatically. Pass undefined where
// other code expects a Bearer token.
const IS_WEB = typeof window !== 'undefined' && window.electron && !window.electron.db;

// IS_WEB is ALSO true on Capacitor native (the shim leaves window.electron.db
// unset). But "cookies carry auth" only holds on the REAL browser build —
// native has no cookie jar for our API origin, so the token MUST be sent
// explicitly as a Bearer header. Conflating the two is what produced the
// cold-start "Access token required": connectDjpal passed `undefined` on native
// and relied on the shim's async-hydrated cache, empty before hydration.
const IS_TRUE_WEB = IS_WEB && !isNative;

const readAccessToken = (auth) =>
    auth.accessToken
    || localStorage.getItem('auth:accessToken')
    || localStorage.getItem('web-shim:dev-access-token')
    || null;
const readRefreshToken = (auth) =>
    auth.refreshToken
    || localStorage.getItem('auth:refreshToken')
    || localStorage.getItem('web-shim:dev-refresh-token')
    || null;

// Decoded JWT payload of a DJ Pal token, or null if malformed.
export const djpalTokenPayload = (token) => {
    try {
        return JSON.parse(atob(token.split('.')[1])) || null;
    } catch {
        return null;
    }
};

// True while the DJ Pal token's own exp is at least `skewMs` away. The token
// is a plain JWT so expiry is knowable client-side — every consumer must
// check it, not just presence: a 30-day token cached in localStorage WILL
// cross its exp inside a long-running session (app left open for days), and
// the server then rejects every call with "Invalid or expired authentication
// token" until something re-exchanges.
export const isDjpalTokenLive = (token, skewMs = 60_000) => {
    const payload = token ? djpalTokenPayload(token) : null;
    if (!payload) return false;
    return !payload.exp || payload.exp * 1000 > Date.now() + skewMs;
};

// The two exact messages music-info-server's auth middleware returns. If one
// of these comes back mid-session the token is dead server-side regardless of
// what its exp claims (secret rotation, revocation) — drop it so the
// background connector re-exchanges instead of failing forever.
export const isDjpalAuthErrorMessage = (msg) =>
    typeof msg === 'string'
    && /invalid or expired authentication token|authentication token is required/i.test(msg);

// Surface a server error on the search UI. Auth-shaped errors additionally
// drop the token, which flips tokenValid and re-arms the background
// connector (useDjpalConnect) — the next interaction gets a fresh exchange
// instead of replaying the dead token forever.
const reportSearchError = (error) => (dispatch) => {
    const classified = classifyError(error);
    if (classified.code === 'AUTH_EXPIRED' || isDjpalAuthErrorMessage(classified.raw)) {
        dispatch(clearToken());
    }
    reportShownError(classified, { surface: 'mixpedia_search' });
    dispatch(setSearchError(classified.display));
};

// userId of the currently logged-in MixGoat account — from the auth user, else
// decoded from the access token. The DJ Pal token is DERIVED from this account
// and carries the SAME userId; comparing them detects a cached DJ Pal token left
// over from a PREVIOUS account.
const mixgoatUserId = (auth) => {
    if (auth?.user?.id) return auth.user.id;
    if (auth?.user?.userId) return auth.user.userId;
    try {
        const at = readAccessToken(auth);
        if (at) return JSON.parse(atob(at.split('.')[1]))?.userId || null;
    } catch { /* malformed access token */ }
    return null;
};

// Single-flight guard: the background connector (useDjpalConnect) and any
// number of patient ensureToken callers can ask for a connection at the same
// moment. Without this each ran its own token exchange — and, on an expired
// access token, its own /api/auth/refresh call. The auth service rate-limits
// refresh to 60/hour/IP, so concurrent + retrying connects could burn the
// whole quota during a hiccup and lock the session out for the rest of the
// hour. One attempt runs at a time; everyone else awaits its result.
let connectInFlight = null;

export const connectDjpal = createAsyncThunk(
    'djpal/connect',
    (_, { dispatch, getState }) => {
        if (!connectInFlight) {
            connectInFlight = doConnectDjpal(dispatch, getState)
                .finally(() => { connectInFlight = null; });
        }
        return connectInFlight;
    }
);

async function doConnectDjpal(dispatch, getState) {
        try {
            dispatch(setTokenError(null));

            // Check if we already have a cached token. Trust it iff it
            // decodes cleanly, carries a userId (added for server-side feed),
            // and the JWT exp hasn't elapsed. We deliberately do NOT call the
            // server validate endpoint here — on web that path proxies into
            // music-info-server which doesn't host /v1/auth/token/validate,
            // so every load was 401-ing and wiping a perfectly good cached
            // token, forcing a re-exchange on every connectDjpal. A real
            // token failure on any subsequent API call will surface a 401
            // and the caller's refresh-and-retry will recover from there.
            const cachedToken = localStorage.getItem('djpal:token');
            if (cachedToken) {
                let usable = false;
                const payload = djpalTokenPayload(cachedToken);
                if (payload) {
                    const hasUserId = !!payload.userId;
                    // 60s skew so we don't hand out a token about to expire.
                    const notExpired = isDjpalTokenLive(cachedToken);
                    // The DJ Pal token is valid for ~30 days and derived from the
                    // MixGoat account. If the MixGoat identity has since changed
                    // (account switch, dev token swap, a logout path that didn't
                    // clear it), the stale token would otherwise be reused for
                    // weeks — serving the PREVIOUS account's likes/follows/feed.
                    // Bind the derived token to its source: reuse only when the
                    // userIds match. Conservative: if the MixGoat userId is
                    // unknown (cold start), treat as a match so a valid session
                    // never breaks; the next 401 + refresh-and-retry recovers.
                    const mgUserId = mixgoatUserId(getState().auth);
                    const sameAccount = !mgUserId || !payload.userId
                        || String(payload.userId) === String(mgUserId);
                    usable = hasUserId && notExpired && sameAccount;
                }
                if (usable) {
                    dispatch(setToken(cachedToken));
                    dispatch(setTokenValid(true));
                    return;
                }
                localStorage.removeItem('djpal:token');
            }

            // Exchange MixGoat access token for a DJ Pal token.
            // Web: undefined — cookies carry mg_access. Auth service accepts cookie.
            // Electron: read from Redux/localStorage as before.
            const { auth } = getState();
            let accessToken = IS_TRUE_WEB ? undefined : readAccessToken(auth);

            // ROOT-CAUSE FIX for the App Review "Access token required":
            // authenticated, but no token in Redux OR localStorage. On native
            // that means iOS evicted the WKWebView token mirror (heavy Sign in
            // with Apple + StoreKit purchase churn) — but the Keychain still has
            // it. Recover from the Keychain before failing, so the exchange
            // never goes out tokenless.
            if (!IS_TRUE_WEB && !accessToken && isNative && window.electron?.auth?.recoverSession) {
                try {
                    const r = await window.electron.auth.recoverSession();
                    if (r?.accessToken) {
                        accessToken = r.accessToken;
                        dispatch(setAccessToken(r.accessToken));
                        if (r.refreshToken) dispatch(setRefreshToken(r.refreshToken));
                        if (r.user && !auth.user) dispatch(setUser(r.user));
                    }
                } catch (_) { /* fall through to the soft error + retry */ }
            }

            if (!IS_TRUE_WEB && !accessToken) {
                // Still nothing — a transient cold-start window (shim not yet
                // hydrated) or a genuine logout. Soft error; the caller's retry
                // loop (gated on auth, slow-retries) recovers once the token
                // lands instead of stranding the tab forever.
                dispatch(setTokenError('Access token required'));
                dispatch(setTokenValid(false));
                return;
            }

            let response = await window.electron.djpal.getToken(accessToken);

            // If the access token is expired/invalid, refresh it and retry.
            // ONLY do this for auth-shaped failures (401/403): a network error
            // or 5xx can't be fixed by a refresh, and each refresh call counts
            // against the auth service's 60/hour/IP rate limit — refreshing on
            // every error let an outage burn the quota and kill the session.
            // Electron's IPC handler now reports the real HTTP status
            // (main.js::djpal:getToken), so undefined means transport/5xx,
            // not "assume auth" — treat it as non-auth-shaped.
            const authShapedError = response.status === 401 || response.status === 403;
            const refreshGateOk = Date.now() - _lastDjpalRefreshAttempt >= MIN_DJPAL_REFRESH_GAP_MS;
            if (response.error && authShapedError && refreshGateOk) {
                _lastDjpalRefreshAttempt = Date.now();
                const refreshToken = IS_TRUE_WEB ? undefined : readRefreshToken(auth);
                if (IS_TRUE_WEB || refreshToken) {
                    const refreshResponse = await window.electron.auth.refreshToken(refreshToken);
                    if (refreshResponse.accessToken) {
                        if (!IS_TRUE_WEB) {
                            accessToken = refreshResponse.accessToken;
                            try { localStorage.setItem('auth:accessToken', accessToken); } catch {}
                        }
                        response = await window.electron.djpal.getToken(accessToken);
                    }
                    // If refresh returned error, fall through to original error
                }
            }

            if (response.error) {
                dispatch(setTokenError(presentError(response.error, { surface: 'djpal_connect' })));
                dispatch(setTokenValid(false));
                return;
            }

            if (response.token) {
                localStorage.setItem('djpal:token', response.token);
                dispatch(setToken(response.token));
                dispatch(setTokenValid(true));
            } else {
                dispatch(setTokenError('Connection failed. Please try again.'));
                dispatch(setTokenValid(false));
            }
        } catch (error) {
            console.error('DJ Pal connect error:', error);
            dispatch(setTokenError(presentError(error, { surface: 'djpal_connect', fallback: 'Connection failed. Please try again.' })));
            dispatch(setTokenValid(false));
        }
}

// Ensure we have a valid token, reconnecting if needed.
//
// Tabs render before the token resolves (background connection via
// useDjpalConnect), so a fast user interaction can land here while the
// token is still being established. Don't fail that interaction — wait for
// it: retry with the same backoff ladder as the background loop (~7.5s
// worst case) so the action's own spinner covers the handshake. In practice
// the first attempt succeeds and this returns immediately.
export const ensureToken = async (dispatch, getState) => {
    // Presence is not enough: the 30-day JWT expires mid-session when the app
    // stays open for days, and the connect loop (gated on tokenValid !== true)
    // never re-checks a token it once validated. Hand out only a live token;
    // an expired one is cleared so connectDjpal re-exchanges instead of
    // trusting the same dead cache.
    const readToken = () => {
        const token = getState().djpal.token || localStorage.getItem('djpal:token');
        if (!token) return null;
        if (isDjpalTokenLive(token)) return token;
        dispatch(clearToken());
        return null;
    };

    let token = readToken();
    for (let attempt = 0; !token && attempt < 6; attempt++) {
        if (attempt > 0) {
            await new Promise((resolve) => setTimeout(resolve, Math.min(500 * attempt, 3000)));
            // The background connector may have landed the token while we slept.
            token = readToken();
            if (token) break;
        }
        try {
            await dispatch(connectDjpal()).unwrap();
        } catch { /* transient — next attempt retries */ }
        token = readToken();
    }

    if (!token) throw new Error('Not connected. Please try again.');
    // Sync to Redux if it was only in localStorage
    if (!getState().djpal.token) dispatch(setToken(token));
    return token;
};

export const searchArtist = createAsyncThunk(
    'djpal/searchArtist',
    async ({ artistName, artistId, artworkUrl, dateRange, preserveResults }, { dispatch, getState }) => {
        try {
            window.electron.analytics.track('mixpedia_artist_search', { query: artistName, artistId: artistId || null });
            track('search_performed', { search_type: 'artist', query: artistName, artistId: artistId || null });
            // The date filter is page-scoped: navigating to a NEW entity
            // (no explicit dateRange passed) clears it — otherwise the pill
            // stayed active across every subsequent Mixpedia page.
            if (dateRange === undefined) dispatch(setDateRange(null));
            dispatch(setIsSearching(true));
            dispatch(setSearchError(null));
            // Record page identity before the request goes out — the URL
            // writer needs it even when this fetch dies offline. See the same
            // call in searchMixes.
            dispatch(setArtistPagePublicId(publicEntityId({ id: artistId })));
            if (!preserveResults) {
                dispatch(setArtistResults(null));
                // Paint a real header (name + any artwork the caller already
                // has) while the page skeleton loads.
                // publicId: the error-banner retry re-dispatches from this
                // record — without the id the retry is name-only.
                dispatch(setPendingArtist({ name: artistName, artworkUrl: artworkUrl || null, publicId: artistId || null }));
                // Show the artist page (its skeleton) at once: from another
                // page kind (label, track, results) a click otherwise looked
                // dead for the whole request (10-20 s on a slow host, 10-20).
                dispatch(setActiveSearch('artist'));
            }

            const token = await ensureToken(dispatch, getState);

            // When the caller knows the artist ID (resolved from artists[] in
            // the feed/likes), pass it as a hint so the backend can skip the
            // name-based lookup and disambiguate same-named artists.
            const byArtistFilters = artistId ? { artist: artistName, artist_id: artistId } : { artist: artistName };
            const includedFilters = artistId ? { artistIncluded: artistName, artist_id: artistId } : { artistIncluded: artistName };

            // Design v2 pages the two supported-artist lists from GET
            // /artists/:id/supported, so the search only needs their top 50 plus
            // totals — the whole list (9k profiles for Guetta) was ~14 s of a
            // 16-34 s first visit (ec 10-31). That route has no date filter, so a
            // date-scoped page keeps the full lists; so does the classic design.
            const supportPaged = DESIGN_V2_AVAILABLE && !!getState().settings?.designV2 && !dateRange;
            const page1 = supportPaged ? { page: 1, limit: 50, supportLimit: 50 } : { page: 1, limit: 50 };
            const [mixesByArtist, mixesIncludingArtist] = await Promise.all([
                window.electron.djpal.searchMixes(token, byArtistFilters, dateRange, page1),
                window.electron.djpal.searchMixes(token, includedFilters, dateRange, page1),
            ]);

            // pendingArtist stays: the artist page keeps its header and the
            // error banner's Retry re-dispatches from it (with the id).
            if (mixesByArtist.error) {
                dispatch(reportSearchError(mixesByArtist.error));
                dispatch(setIsSearching(false));
                return;
            }
            if (mixesIncludingArtist.error) {
                dispatch(reportSearchError(mixesIncludingArtist.error));
                dispatch(setIsSearching(false));
                return;
            }

            const byArtistData = mixesByArtist.data || mixesByArtist;
            const includedData = mixesIncludingArtist.data || mixesIncludingArtist;

            // Handle {results: [...]}, {result: [...]}, and direct array formats
            const extractMixes = (d) => {
                const arr = d.results || d.result;
                if (Array.isArray(arr)) return arr;
                if (Array.isArray(d)) return d;
                return [];
            };

            dispatch(setArtistResults({
                artistName,
                artist: byArtistData.artist || includedData.artist || null,
                mixesByArtist: extractMixes(byArtistData),
                mixesIncludingArtist: extractMixes(includedData),
                statsByArtist: byArtistData.stats || {},
                statsIncluded: includedData.stats || {},
                supportingArtists: includedData.supportingArtists || [],
                artistsSupporting: byArtistData.artistsSupporting || [],
                // A server without supportLimit sends the full arrays and no
                // totals; supportPaged then falls back to the arrays' lengths.
                supportingArtistsTotal: includedData.supportingArtistsTotal ?? (includedData.supportingArtists || []).length,
                artistsSupportingTotal: byArtistData.artistsSupportingTotal ?? (byArtistData.artistsSupporting || []).length,
                supportPaged,
                paginationByArtist: byArtistData.pagination ? { ...byArtistData.pagination, hasNext: byArtistData.pagination.page < byArtistData.pagination.totalPages } : null,
                paginationIncluding: includedData.pagination ? { ...includedData.pagination, hasNext: includedData.pagination.page < includedData.pagination.totalPages } : null,
            }));
            dispatch(setPendingArtist(null));
            dispatch(setIsSearching(false));

            // Replace any optimistic placeholder recent-search entry (which
            // had no publicId or artwork because the caller didn't have them
            // yet) with a canonical one keyed by the resolved publicId. Only
            // fires when a placeholder for this name already exists so we
            // don't add history rows for non-search entry points (feed
            // clicks, sidebar links, auto-discover, etc).
            const resolvedArtist = byArtistData.artist || includedData.artist;
            const resolvedPublicId = entityId(resolvedArtist);
            const isAuthenticated = getState().auth?.isAuthenticated;
            const placeholderExists = (() => {
                if (!artistName) return false;
                const items = getState().searchHistory?.items || [];
                const lc = artistName.toLowerCase();
                return items.some(e =>
                    e.entityType === 'artist'
                    && (e.surface || 'mixpedia') === 'mixpedia'
                    && (e.entityName || '').toLowerCase() === lc
                    && String(e.entityPublicId || '').startsWith('name:'));
            })();
            if (isAuthenticated && artistName && resolvedPublicId && placeholderExists) {
                dispatch(addSearchHistoryEntry({
                    entityType: 'artist',
                    entityPublicId: resolvedPublicId,
                    entityName: resolvedArtist.name || artistName,
                    entityArtwork: resolvedArtist.artworkUrl || null,
                    surface: 'mixpedia',
                }));
            }
        } catch (error) {
            console.error('DJ Pal artist search error:', error);
            dispatch(setSearchError(presentError(error, { surface: 'mixpedia_search', fallback: 'Artist search failed. Please try again.' })));
            dispatch(setIsSearching(false));
        }
    }
);

export const loadMoreArtistMixes = createAsyncThunk(
    'djpal/loadMoreArtistMixes',
    async ({ section, filters, dateRange, page }, { dispatch, getState }) => {
        try {
            const token = await ensureToken(dispatch, getState);
            // supportLimit 1: a later page never reads the supported-artist
            // lists, so don't make the server build them (14 s for Guetta).
            const result = await window.electron.djpal.searchMixes(
                token, filters, dateRange, { page, limit: 50, supportLimit: 1 }
            );

            if (result.error) return;

            const data = result.data || result;
            const extractMixes = (d) => {
                const arr = d.results || d.result;
                if (Array.isArray(arr)) return arr;
                if (Array.isArray(d)) return d;
                return [];
            };

            const mixes = extractMixes(data);
            const pagination = data.pagination
                ? { ...data.pagination, hasNext: data.pagination.page < data.pagination.totalPages }
                : null;

            if (mixes.length > 0) {
                dispatch(appendArtistMixes({ section, mixes, pagination }));
                // Artist-page mixes "Show more": covers with the data, same
                // rule as the track lists. Mixes resolve via nativeMixCover
                // on render, but on desktop that is artworkUrl/artwork/image.
                warmTrackListCovers(mixes);
            } else if (pagination) {
                dispatch(appendArtistMixes({ section, mixes: [], pagination }));
            }
        } catch (error) {
            console.error('Load more artist mixes error:', error);
        }
    }
);

// Fetches mixes that contain a given track, optionally filtered to mixes
// where a specific artist is the DJ. Used by the track-context-menu
// "View in <artist>'s mixes" / "Show all DJs who played this" actions.
export const fetchTrackMixAppearances = createAsyncThunk(
    'djpal/fetchTrackMixAppearances',
    async ({ trackPublicId, trackId, artistPublicId, artistName, artistId, excludeArtistId, page = 1, limit = 50 }, { dispatch, getState }) => {
        const token = await ensureToken(dispatch, getState);
        const result = await window.electron.djpal.searchTrackMixAppearances(token, {
            trackPublicId,
            trackId,
            artistPublicId,
            artistName,
            artistId,
            excludeArtistId, // v2: drop the mixes this artist DJ'd ("played by others"); v1 ignores it
            page,
            limit,
        });

        if (result?.error) {
            throw new Error(errMsg(result.error));
        }

        // IPC handler forwards the server's `res.paginated()` envelope as-is
        // (`{ data: [...], pagination: {...}, meta }`). Fall back to the
        // legacy `{ results, pagination }` shape for any other path.
        const data = result?.data ?? result ?? {};
        const mixes = Array.isArray(data) ? data : (Array.isArray(data.results) ? data.results : []);
        const rawPagination = result?.pagination || data?.pagination || null;
        // Normalize the field name: `res.paginated()` emits `total`,
        // legacy mix endpoints emit `totalCount`. Expose both so callers
        // can read either without guessing which path the response came
        // through.
        const pagination = rawPagination
            ? {
                ...rawPagination,
                total: rawPagination.total ?? rawPagination.totalCount,
                totalCount: rawPagination.totalCount ?? rawPagination.total,
                hasNext: rawPagination.page < rawPagination.totalPages,
            }
            : null;

        return { mixes, pagination };
    }
);

// Fetches a paginated, substring-filtered slice of tracks for an artist.
// relation='played' returns tracks the artist played as a DJ;
// relation='by' returns tracks the artist authored (as track artist or
// remixer) that have appeared in any mix. Used by the track-card search
// inputs on the artist page.
export const fetchFilteredArtistTracks = createAsyncThunk(
    'djpal/fetchFilteredArtistTracks',
    async ({ relation, artistName, artistPublicId, query, page = 1, limit = 50 }, { dispatch, getState }) => {
        const token = await ensureToken(dispatch, getState);
        const result = await window.electron.djpal.searchArtistTracks(token, {
            artistName,
            artistPublicId,
            relation,
            q: query,
            page,
            limit,
        });

        if (result?.error) {
            throw new Error(errMsg(result.error));
        }

        // IPC handler forwards the server's `res.paginated()` envelope as-is
        // (`{ data: [...], pagination: {...}, meta }`).
        const data = result?.data ?? result ?? {};
        const tracks = Array.isArray(data) ? data : (Array.isArray(data.results) ? data.results : []);
        const rawPagination = result?.pagination || data?.pagination || null;
        // Normalize the field name: `res.paginated()` emits `total`,
        // legacy mix endpoints emit `totalCount`. Expose both so callers
        // can read either without guessing which path the response came
        // through.
        const pagination = rawPagination
            ? {
                ...rawPagination,
                total: rawPagination.total ?? rawPagination.totalCount,
                totalCount: rawPagination.totalCount ?? rawPagination.total,
                hasNext: rawPagination.page < rawPagination.totalPages,
            }
            : null;

        return { tracks, pagination };
    }
);

// Fetches a paginated, title-filtered slice of an artist's mixes for the
// in-card search UI. Returns { mixes, pagination } without mutating the
// global artistResults — callers manage display state locally so the
// underlying unfiltered list (used by derived track cards) is preserved.
export const fetchFilteredArtistMixes = createAsyncThunk(
    'djpal/fetchFilteredArtistMixes',
    async ({ section, artistName, artistId, query, dateRange, page = 1, limit = 50, baseFilters: override = null }, { dispatch, getState }) => {
        // `override` = a non-artist primary filter (an event / venue / series
        // page passes { title }). The server allows one title filter, so the
        // modal's search box filters those rows client-side instead.
        const baseFilters = override || (section === 'including'
            ? { artistIncluded: artistName }
            : { artist: artistName });
        const filters = {
            ...baseFilters,
            ...(artistId && !override ? { artist_id: artistId } : {}),
            ...(query && !override ? { title: query } : {}),
        };

        const token = await ensureToken(dispatch, getState);
        const result = await window.electron.djpal.searchMixes(
            token, filters, dateRange, { page, limit }
        );

        if (result.error) {
            throw new Error(errMsg(result.error));
        }

        const data = result.data || result;
        const arr = data.results || data.result;
        const mixes = Array.isArray(arr) ? arr : (Array.isArray(data) ? data : []);
        const pagination = data.pagination
            ? { ...data.pagination, hasNext: data.pagination.page < data.pagination.totalPages }
            : null;

        return { mixes, pagination };
    }
);

export const searchTrack = createAsyncThunk(
    'djpal/searchTrack',
    async ({ trackName, trackId, variant, dateRange, preserveResults, artworkUrl }, { dispatch, getState }) => {
        try {
            if (dateRange === undefined) dispatch(setDateRange(null)); // page-scoped filter (see searchArtist)
            window.electron.analytics.track('mixpedia_track_search', { query: trackName });
            track('search_performed', { search_type: 'track', query: trackName });
            dispatch(setIsSearching(true));
            dispatch(setSearchError(null));
            dispatch(setTrackPagePublicId(publicEntityId({ id: trackId })));
            if (!preserveResults) {
                dispatch(setTrackResults(null));
                dispatch(clearTrackVariantCache());
                // Pending header artwork while the skeleton loads. Always set
                // (even null) so a previous track's art can't linger.
                dispatch(setPendingTrack({ name: trackName, artworkUrl: artworkUrl || null }));
            }

            const token = await ensureToken(dispatch, getState);

            // Prefer the catalog id when the caller has one: exact seed (no
            // normalized-name miss, no dupe-row shadowing). _trackName lets the
            // server fall back to the name search when the id yields nothing
            // (stale client id). Requires the music-info-server whose trackId
            // branch returns the full track-page payload — deploy it with (or
            // before) this client.
            const mixFilters = trackId
                ? { trackId, _trackName: trackName }
                : { trackIncluded: trackName };
            const recsSeed = trackId ? { trackName, trackId } : trackName;

            // Variant B (previous embedding model) only feeds the localhost-only
            // V7/V8 comparison toggle — never fetch it in prod, and never let it
            // gate first paint's Promise.all.
            const fetchVariantB = isRecsVariantDevEnabled();
            const [mixesResult, recsA, recsB] = await Promise.all([
                window.electron.djpal.searchMixes(token, mixFilters, dateRange),
                window.electron.djpal.searchTrackRecommendations(token, recsSeed, 'NODE2VEC', 'A', dateRange),
                fetchVariantB
                    ? window.electron.djpal.searchTrackRecommendations(token, recsSeed, 'NODE2VEC', 'B', dateRange)
                    : Promise.resolve(null),
            ]);

            if (mixesResult.error) {
                dispatch(reportSearchError(mixesResult.error));
                dispatch(setIsSearching(false));
                return;
            }

            // Normalize cached variant data
            const normalizeRecs = (raw) => {
                if (!raw || raw.error) return null;
                const d = raw.data || raw;
                return {
                    trackInfo: d.result?.track || null,
                    frequentlyPlayedTogether: d.frequentlyPlayedTogether || [],
                    relatedTracks: (d.relatedTracks || []).map(r => r.track || r),
                    stats: d.stats || {},
                };
            };

            dispatch(setTrackVariantCache({ variant: 'A', data: normalizeRecs(recsA) }));
            if (recsB) dispatch(setTrackVariantCache({ variant: 'B', data: normalizeRecs(recsB) }));

            const activeRecs = (variant === 'B' && recsB) ? recsB : recsA;
            const recsData = (!activeRecs || activeRecs.error) ? {} : (activeRecs.data || activeRecs);
            const mixesData = mixesResult.data || mixesResult;

            // Tripwire: asked for track X by exact id, got track Y back —
            // the "click one track, get another" class (the 2026-08-05
            // parseInt chimera shipped for a week and only user eyes caught
            // it). Server is authoritative so we still render, but the
            // mismatch surfaces in PostHog instead of waiting for a report.
            const seedEcho = recsData.result?.track && entityId(recsData.result.track);
            if (trackId && seedEcho && String(seedEcho) !== String(trackId)) {
                console.error('[searchTrack] seed mismatch: requested', trackId, 'got', seedEcho);
                track('track_page_seed_mismatch', { requested_id: String(trackId), returned_id: String(seedEcho) });
            }

            const trackMixArr = mixesData.results || mixesData.result;
            const trackMixes = Array.isArray(trackMixArr) ? trackMixArr : (Array.isArray(mixesData) ? mixesData : []);

            dispatch(setTrackResults({
                trackName,
                mixes: trackMixes,
                trackInfo: recsData.result?.track || null,
                frequentlyPlayedTogether: recsData.frequentlyPlayedTogether || [],
                relatedTracks: (recsData.relatedTracks || []).map(r => r.track || r),
                supportingArtists: mixesData.supportingArtists || [],
                stats: { ...(mixesData.stats || {}), ...(recsData.stats || {}) },
            }));
            dispatch(setIsSearching(false));
        } catch (error) {
            console.error('DJ Pal track search error:', error);
            dispatch(setSearchError(presentError(error, { surface: 'mixpedia_search', fallback: 'Track search failed. Please try again.' })));
            dispatch(setIsSearching(false));
        }
    }
);

export const searchMixes = createAsyncThunk(
    'djpal/searchMixes',
    // `paginate`: force pagination on a title search (event/venue clicks land
    // on hundreds of mixes — without it title searches cap at the server's 50).
    // `lite`: no per-mix tracklists (event / venue / source pages list sets; the
    // tracklist is fetched on expand). 50 mixes with tracklists cost 20 s cold on
    // the shared host; the lite page is a few hundred ms.
    async ({ title, filters, dateRange, fallbackToTitle, artistContextName, paginate = false, lite = false }, { dispatch, getState }) => {
        try {
            dispatch(setIsSearching(true));
            dispatch(setSearchError(null));
            dispatch(setMixResults(null));
            dispatch(setMixResultsPagination(null));
            dispatch(clearExpandedMixIds());
            // Caller sets mixFilterContext immediately before dispatching
            // when the search comes from a "Show all by/featuring" click.
            // For free-text searches (title-only or artist+title), clear any
            // stale context. For bare-name searches (artistContextName), defer
            // the decision until we see whether the artist filter has hits —
            // setting it eagerly would flash "Mixes by Sunset Set" before
            // falling back to title.
            const isTitleSearch = !filters || (!!filters.title && !filters.artistIncluded);
            // Direct id opens (autocomplete/history mix clicks) are a fresh
            // destination too — a stale "Mixes by X" context must not survive.
            // paginate=true marks an event/venue click whose CALLER set the
            // context deliberately — clearing it here stripped the page title.
            // All three exact-identity keys count (id / public_id / link) —
            // click paths use each of them.
            const isDirectOpen = !!(filters?.id || filters?.public_id || filters?.link);
            if (!artistContextName && ((isTitleSearch && !paginate) || isDirectOpen)) dispatch(setMixFilterContext(null));

            // Record the mix-page identity NOW, not from the response: the URL
            // writer needs it even if this request fails (offline), so the
            // refresh that follows re-opens the same mix by id instead of
            // text-searching the slugs. The reducer drops numeric values.
            dispatch(setMixPagePublicId(publicEntityId({ public_id: filters?.public_id, id: filters?.id })));

            const token = await ensureToken(dispatch, getState);

            let searchFilters = filters || { title };
            let pagination = (isTitleSearch && !paginate) ? null : { page: 1, limit: 50 };
            const call = lite ? window.electron.djpal.searchMixesLite : window.electron.djpal.searchMixes;
            let result = await call(token, searchFilters, dateRange, pagination);

            if (result.error) {
                dispatch(reportSearchError(result.error));
                dispatch(setIsSearching(false));
                return;
            }

            let data = result.data || result;
            let mixArr = data.results || data.result;
            let mixes = Array.isArray(mixArr) ? mixArr : (Array.isArray(data) ? data : []);

            // Bare-name fallback: caller asked "is this an artist or a title?"
            // by passing { artist: q } + fallbackToTitle. If artist returned 0,
            // retry as title. Paginate the title search and set a 'title' filter
            // context so infinite-scroll engages — events like "Tomorrowland"
            // have hundreds of mixes and were otherwise capped at the server
            // default of 50.
            // Also covers `{ artist, title }` — the slug-based mix-page URL
            // fallback, where a hyphen in the real DJ name doesn't survive
            // slugging and the artist match comes back empty. Retrying on the
            // title lands on a usable page instead of a blank one.
            if (fallbackToTitle && mixes.length === 0 && filters?.artist) {
                const q = filters.title || filters.artist;
                searchFilters = { title: q };
                pagination = { page: 1, limit: 50 };
                result = await call(token, searchFilters, dateRange, pagination);
                if (result.error) {
                    dispatch(reportSearchError(result.error));
                    dispatch(setIsSearching(false));
                    return;
                }
                data = result.data || result;
                mixArr = data.results || data.result;
                mixes = Array.isArray(mixArr) ? mixArr : (Array.isArray(data) ? data : []);
                if (mixes.length > 0) {
                    dispatch(setMixFilterContext({ kind: 'title', titleQuery: q }));
                } else {
                    dispatch(setMixFilterContext(null));
                }
            } else if (artistContextName) {
                if (mixes.length > 0) {
                    dispatch(setMixFilterContext({ kind: 'by', artistName: artistContextName, artistPublicId: filters?.artist_id || null }));
                } else {
                    dispatch(setMixFilterContext(null));
                }
            }

            dispatch(setMixResults(mixes));
            if (data.pagination) {
                dispatch(setMixResultsPagination({
                    ...data.pagination,
                    hasNext: data.pagination.page < data.pagination.totalPages,
                }));
            }
            dispatch(setIsSearching(false));
        } catch (error) {
            console.error('DJ Pal mix search error:', error);
            dispatch(setSearchError(presentError(error, { surface: 'mixpedia_search', fallback: 'Mix search failed. Please try again.' })));
            dispatch(setIsSearching(false));
        }
    }
);

export const loadMoreMixes = createAsyncThunk(
    'djpal/loadMoreMixes',
    async ({ filters, dateRange, page, lite = false }, { dispatch, getState }) => {
        try {
            const token = await ensureToken(dispatch, getState);
            const result = await (lite ? window.electron.djpal.searchMixesLite : window.electron.djpal.searchMixes)(
                token, filters, dateRange, { page, limit: 50 }
            );
            if (result.error) return;

            const data = result.data || result;
            const arr = data.results || data.result;
            const mixes = Array.isArray(arr) ? arr : (Array.isArray(data) ? data : []);
            const pagination = data.pagination
                ? { ...data.pagination, hasNext: data.pagination.page < data.pagination.totalPages }
                : null;

            if (mixes.length > 0 || pagination) {
                dispatch(appendMixResults({ mixes, pagination }));
            }
        } catch (error) {
            console.error('Load more mixes error:', error);
        }
    }
);

// Fetch a single mix's full tracklist by publicId. Used when the mix-results
// list came from an `artistIncluded` search (which returns only `tracksIncluded`)
// and the user expands a mix to see the complete tracklist.
export const fetchFullMixTracklist = createAsyncThunk(
    'djpal/fetchFullMixTracklist',
    async ({ mixId, publicId }, { dispatch, getState }) => {
        if (!publicId) return;
        try {
            const token = await ensureToken(dispatch, getState);
            const result = await window.electron.djpal.searchMixes(token, { public_id: publicId });
            if (result.error) return;

            const data = result.data || result;
            const arr = data.results || data.result;
            const mix = Array.isArray(arr) ? arr[0] : (Array.isArray(data) ? data[0] : null);
            // Always resolve — an empty array marks "fetched, genuinely no
            // tracklist" so the UI can distinguish loading (undefined) from
            // known-empty and stop showing a loading state.
            dispatch(updateMixTracklist({ mixId, tracklist: Array.isArray(mix?.tracklist) ? mix.tracklist : [] }));
        } catch (error) {
            console.error('Fetch full mix tracklist error:', error);
        }
    }
);

// Paginated track list for the "Show all tracks" view. First page replaces
// results; subsequent pages append via loadMoreTracksList.
export const searchTracksList = createAsyncThunk(
    'djpal/searchTracksList',
    async ({ query, dateRange }, { dispatch, getState }) => {
        try {
            window.electron.analytics.track('mixpedia_track_list_search', { query });
            track('search_performed', { search_type: 'track_list', query });
            dispatch(setIsSearching(true));
            dispatch(setSearchError(null));
            dispatch(setTrackListResults(null));
            dispatch(setTrackListPagination(null));
            // FTS searches drop any "tracks played by DJ" context so the
            // load-more path uses the correct endpoint.
            dispatch(setTrackListContext(null));

            const token = await ensureToken(dispatch, getState);
            const result = await window.electron.djpal.searchTracks(token, query, 1, 50, dateRange);

            if (result.error) {
                dispatch(reportSearchError(result.error));
                dispatch(setIsSearching(false));
                return;
            }

            const data = result.data || result;
            const arr = data.results || [];
            dispatch(setTrackListResults(Array.isArray(arr) ? arr : []));
            warmTrackListCovers(arr);
            if (data.pagination) {
                dispatch(setTrackListPagination({
                    ...data.pagination,
                    hasNext: data.pagination.page < data.pagination.totalPages,
                }));
            }
            dispatch(setIsSearching(false));
        } catch (error) {
            console.error('DJ Pal track list search error:', error);
            dispatch(setSearchError(presentError(error, { surface: 'mixpedia_search', fallback: 'Track search failed. Please try again.' })));
            dispatch(setIsSearching(false));
        }
    }
);

export const loadMoreTracksList = createAsyncThunk(
    'djpal/loadMoreTracksList',
    async ({ query, page, dateRange }, { dispatch, getState }) => {
        try {
            const token = await ensureToken(dispatch, getState);
            // Honor the active context: an artist-tracks view (Show all on the
            // artist page) paginates through the relation-ordered endpoint so
            // page 2+ keeps the same MAX(mix date) ordering; everything else
            // uses FTS.
            const ctx = getState().djpal.trackListContext;
            const result = ctx?.kind === 'artistTracks'
                ? await window.electron.djpal.searchArtistTracks(token, {
                    artistName: ctx.artistName,
                    artistPublicId: ctx.artistPublicId,
                    relation: ctx.relation || 'played',
                    q: '',
                    page,
                    limit: 50,
                })
                : ctx?.kind === 'playedBy'
                ? await window.electron.djpal.searchTracksPlayedBy(token, {
                    artistPublicId: ctx.artistPublicId,
                    artistName: ctx.artistName,
                    page,
                    limit: 50,
                    dateRange,
                })
                : await window.electron.djpal.searchTracks(token, query, page, 50, dateRange);
            if (result.error) return;

            const data = result.data || result;
            const tracks = Array.isArray(data) ? data : (data.results || []);
            const rawPag = result.pagination || data.pagination || null;
            const pagination = rawPag
                ? { ...rawPag, total: rawPag.total ?? rawPag.totalCount, hasNext: rawPag.page < rawPag.totalPages }
                : null;

            if (tracks.length > 0 || pagination) {
                dispatch(appendTrackListResults({ tracks, pagination }));
                warmTrackListCovers(tracks);
            }
        } catch (error) {
            console.error('Load more tracks list error:', error);
        }
    }
);

// "Show all" for the artist-page track cards. Both relations go through the
// artist-tracks endpoint (`searchArtistTracks` → findArtistTracks), which
// orders by MAX(mix date) DESC — the SAME order as the on-page preview
// (getUniqueTracks over date-DESC mixes). So "Show all" CONTINUES the list
// instead of reshuffling it.
//   relation 'played' = tracks the artist played as a DJ  ("Tracks they played")
//   relation 'by'     = the artist's own tracks that appeared in mixes
//                       ("Their tracks played by others")
// `trackListContext` carries the relation so the load-more path keeps hitting
// the same endpoint with the same ordering.
export const searchTracksPlayedByArtist = createAsyncThunk(
    'djpal/searchTracksPlayedByArtist',
    async ({ artistName, artistPublicId, dateRange, relation = 'played' }, { dispatch, getState }) => {
        try {
            window.electron.analytics.track('mixpedia_tracks_played_by', { artistName, artistPublicId, relation });
            track('tracks_played_by_view', { artistName, artistPublicId, relation });
            dispatch(setIsSearching(true));
            dispatch(setSearchError(null));
            dispatch(setTrackListResults(null));
            dispatch(setTrackListPagination(null));
            dispatch(setTrackListContext({ kind: 'artistTracks', relation, artistName, artistPublicId: artistPublicId || null }));

            const token = await ensureToken(dispatch, getState);
            const result = await window.electron.djpal.searchArtistTracks(token, {
                artistName, artistPublicId, relation, q: '', page: 1, limit: 50,
            });

            if (result?.error) {
                dispatch(reportSearchError(result.error));
                dispatch(setIsSearching(false));
                return;
            }

            // `res.paginated()` envelope: { data: [...], pagination: {...} }.
            const data = result?.data ?? result ?? {};
            const arr = Array.isArray(data) ? data : (Array.isArray(data.results) ? data.results : []);
            dispatch(setTrackListResults(arr));
            warmTrackListCovers(arr);
            const raw = result?.pagination || data?.pagination || null;
            if (raw) {
                dispatch(setTrackListPagination({
                    ...raw,
                    total: raw.total ?? raw.totalCount,
                    hasNext: raw.page < raw.totalPages,
                }));
            }
            dispatch(setIsSearching(false));
        } catch (error) {
            console.error('searchTracksPlayedByArtist error:', error);
            dispatch(setSearchError(presentError(error, { surface: 'mixpedia_search', fallback: 'Track lookup failed. Please try again.' })));
            dispatch(setIsSearching(false));
        }
    }
);

// Tracks-tab entry point. Free-text Enter from the tab's search bar lands here
// and decides which view to show. The single-track endpoints require an
// "Artist - Title" form (trackIncluded matches t.artist_title_concat exactly,
// and the recommendations endpoint resolves a specific track), so a bare
// query like "radio slave" can only ever resolve via FTS — skip the
// single-track calls in that case to avoid wasted work.
const TRACK_FORM_RE = /\S\s+[-–—]\s+\S/;

export const runTrackTabSearch = createAsyncThunk(
    'djpal/runTrackTabSearch',
    async ({ query, variant, dateRange, artworkUrl }, { dispatch, getState }) => {
        const trimmed = (query || '').trim();
        if (!trimmed) return;

        const isFullTrackForm = TRACK_FORM_RE.test(trimmed);

        try {
            window.electron.analytics.track('mixpedia_track_tab_search', {
                query: trimmed,
                isFullTrackForm,
            });
            track('search_performed', {
                search_type: 'track_tab',
                query: trimmed,
                isFullTrackForm,
            });
            dispatch(setIsSearching(true));
            dispatch(setSearchError(null));
            // Clear both views so we don't render stale data while routing.
            dispatch(setTrackResults(null));
            dispatch(clearTrackVariantCache());
            dispatch(setTrackListResults(null));
            dispatch(setTrackListPagination(null));
            // Free-text search clears any "tracks played by DJ" context.
            dispatch(setTrackListContext(null));
            // Switch to the likely destination view NOW so its skeleton shows
            // while we fetch — leaving the previous page mounted put its
            // refinement loader on screen for the whole routing fetch.
            if (isFullTrackForm) {
                dispatch(setTrackQuery(trimmed));
                dispatch(setPendingTrack({ name: trimmed, artworkUrl: artworkUrl || null }));
                dispatch(setActiveSearch('track'));
            } else {
                dispatch(setTrackListQuery(trimmed));
                dispatch(setActiveSearch('trackList'));
            }

            const token = await ensureToken(dispatch, getState);

            if (!isFullTrackForm) {
                const listResult = await window.electron.djpal.searchTracks(token, trimmed, 1, 50, dateRange);
                if (listResult.error) {
                    dispatch(reportSearchError(listResult.error));
                    return;
                }
                const data = listResult.data || listResult;
                const arr = data.results || [];
                dispatch(setTrackListResults(Array.isArray(arr) ? arr : []));
                warmTrackListCovers(arr);
                if (data.pagination) {
                    dispatch(setTrackListPagination({
                        ...data.pagination,
                        hasNext: data.pagination.page < data.pagination.totalPages,
                    }));
                }
                dispatch(setTrackListQuery(trimmed));
                dispatch(setActiveSearch('trackList'));
                return;
            }

            // "Artist - Title" form: try the single-track view AND fetch the
            // FTS list as a fallback in parallel. The list call is cheap and
            // guarantees we have data ready if the track doesn't resolve.
            // Variant B (previous embedding model) is dev-only — see searchTrack.
            const fetchVariantB = isRecsVariantDevEnabled();
            const [mixesResult, recsA, recsB, listResult] = await Promise.all([
                window.electron.djpal.searchMixes(token, { trackIncluded: trimmed }, dateRange),
                window.electron.djpal.searchTrackRecommendations(token, trimmed, 'NODE2VEC', 'A', dateRange),
                fetchVariantB
                    ? window.electron.djpal.searchTrackRecommendations(token, trimmed, 'NODE2VEC', 'B', dateRange)
                    : Promise.resolve(null),
                window.electron.djpal.searchTracks(token, trimmed, 1, 50, dateRange),
            ]);

            const mixesData = mixesResult.error ? null : (mixesResult.data || mixesResult);
            const trackMixArr = mixesData?.results || mixesData?.result;
            const trackMixes = Array.isArray(trackMixArr)
                ? trackMixArr
                : (Array.isArray(mixesData) ? mixesData : []);

            const normalizeRecs = (raw) => {
                if (!raw || raw.error) return null;
                const d = raw.data || raw;
                return {
                    trackInfo: d.result?.track || null,
                    frequentlyPlayedTogether: d.frequentlyPlayedTogether || [],
                    relatedTracks: (d.relatedTracks || []).map(r => r.track || r),
                    stats: d.stats || {},
                };
            };
            const activeRecsRaw = (variant === 'B' && recsB) ? recsB : recsA;
            const recsData = (!activeRecsRaw || activeRecsRaw.error)
                ? {}
                : (activeRecsRaw.data || activeRecsRaw);
            const trackInfo = recsData.result?.track || null;
            const trackResolved = !!trackInfo || trackMixes.length > 0;

            if (trackResolved) {
                dispatch(setTrackVariantCache({ variant: 'A', data: normalizeRecs(recsA) }));
                if (recsB) dispatch(setTrackVariantCache({ variant: 'B', data: normalizeRecs(recsB) }));
                dispatch(setTrackResults({
                    trackName: trimmed,
                    mixes: trackMixes,
                    trackInfo,
                    frequentlyPlayedTogether: recsData.frequentlyPlayedTogether || [],
                    relatedTracks: (recsData.relatedTracks || []).map(r => r.track || r),
                    supportingArtists: mixesData?.supportingArtists || [],
                    stats: { ...(mixesData?.stats || {}), ...(recsData.stats || {}) },
                }));
                dispatch(setTrackQuery(trimmed));
                dispatch(setActiveSearch('track'));
                return;
            }

            // Track didn't resolve — use the FTS list result we already fetched.
            if (listResult.error) {
                dispatch(reportSearchError(listResult.error));
                return;
            }
            const listData = listResult.data || listResult;
            const listArr = listData.results || [];
            dispatch(setTrackListResults(Array.isArray(listArr) ? listArr : []));
            if (listData.pagination) {
                dispatch(setTrackListPagination({
                    ...listData.pagination,
                    hasNext: listData.pagination.page < listData.pagination.totalPages,
                }));
            }
            dispatch(setTrackListQuery(trimmed));
            dispatch(setActiveSearch('trackList'));
        } catch (error) {
            console.error('runTrackTabSearch error:', error);
            dispatch(setSearchError(presentError(error, { surface: 'mixpedia_search', fallback: 'Track search failed. Please try again.' })));
        } finally {
            dispatch(setIsSearching(false));
        }
    }
);
